import React from "react"

import { Form, Formik, FormikHelpers } from "formik"
import { gql } from "urql"
import * as Yup from "yup"

import { weekdayOptions } from "src/enums"

import { useMutation } from "hooks/urql"

import { DateObject } from "components/Forms/DatePicker"
import { ErrorMessage, RadioGroup } from "components/Forms/Formik"
import {
  DateRangeField,
  FormFailures,
  SubmitButton,
  TimeField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

type GenerateClassPeriodsProps = {
  acrClassId: string
  timeZone: string
}

type GenerateClassPeriodsFormState = {
  dates: DateObject[]
  startsAt: DateObject
  endsAt: DateObject
  weekdays: string[]
}

const ValidationSchema = Yup.object().shape({
  dates: Yup.array().min(2, "Start and end dates are required"),
  startsAt: Yup.mixed().required("Start time is required"),
  endsAt: Yup.mixed().required("End time is required"),
  weekdays: Yup.array().min(1, "Select at least one weekday"),
})

const GenerateClassPeriods: React.FC<GenerateClassPeriodsProps> = ({
  acrClassId,
  timeZone,
}) => {
  const [, generatePeriods]: any[] = useMutation(generateClassPeriodsMutation)

  const formState: GenerateClassPeriodsFormState = {
    dates: [],
    startsAt: new DateObject().setHour(9).setMinute(0).setSecond(0),
    endsAt: new DateObject().setHour(10).setMinute(0).setSecond(0),
    weekdays: [],
  }

  const handleSubmit = (
    values: GenerateClassPeriodsFormState,
    actions: FormikHelpers<GenerateClassPeriodsFormState>
  ) => {
    const { dates, startsAt, endsAt, weekdays } = values

    generatePeriods({
      acrClassId,
      startsOn: dates[0].format("YYYY-MM-DD"),
      endsOn: dates[1].format("YYYY-MM-DD"),
      weekdays,
      startTime: { hour: startsAt.hour, minute: startsAt.minute },
      endTime: { hour: endsAt.hour, minute: endsAt.minute },
    })
      .then((result: any) => {
        const failures = result.data?.generateAcrClassPeriods?.failures
        if (failures?.length > 0) {
          handleFailure(actions, failures)
        } else {
          window.location.reload()
        }
      })
      .catch(() => handleFailure(actions))
  }

  return (
    <ModalWithProvidedBody
      modalTitle="Generate Periods"
      buttonText="Generate Periods"
      buttonClassName="btn btn-info"
    >
      {({ closeModal }: { closeModal: () => void }) => (
        <Formik
          initialValues={formState}
          validationSchema={ValidationSchema}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  Times are in teacher's timezone: {timeZone}
                </p>

                <DateRangeField name="dates" label="Date Range" />
                <TimeField name="startsAt" label="Start Time" />
                <TimeField name="endsAt" label="End Time" />

                <div className="form-group">
                  <label className="form-label">Days of Week</label>
                  <RadioGroup
                    type="checkbox"
                    name="weekdays"
                    values={weekdayOptions}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { value, checked } = e.currentTarget
                      setFieldValue(
                        "weekdays",
                        checked
                          ? [...values.weekdays, value]
                          : values.weekdays.filter(weekday => weekday !== value)
                      )
                    }}
                  />
                  <ErrorMessage name="weekdays" />
                </div>
              </div>

              <div className="modal-footer flex-column">
                <FormFailures />
                <div className="d-flex justify-content-between w-full">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-danger"
                  >
                    Cancel
                  </button>
                  <SubmitButton text="Generate" />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const generateClassPeriodsMutation = gql`
  mutation (
    $acrClassId: ID!
    $startsOn: Date!
    $endsOn: Date!
    $weekdays: [WeekdayEnum!]!
    $startTime: TimeOfDayInputObject!
    $endTime: TimeOfDayInputObject!
  ) {
    generateAcrClassPeriods(
      acrClassId: $acrClassId
      startsOn: $startsOn
      endsOn: $endsOn
      weekdays: $weekdays
      startTime: $startTime
      endTime: $endTime
    ) {
      failures {
        message
      }
    }
  }
`

export default GenerateClassPeriods
